import { motion } from 'framer-motion'
import { RevealLines } from './Reveal'
import DemoPreview from './DemoPreview'
import Seo, { SITE } from './Seo'
import { L, useLocale, useT } from '../i18n/locale.jsx'
import { getDepartment } from '../data/departments'
import { getSeo } from '../i18n/seo'
import { BOOKING_URL } from '../config'
import { calAttrs } from '../cal'

const EASE = [0.16, 1, 0.3, 1]
// Cod scurt de piață (RO/CH) — consistent cu secțiunea What we build; steagurile
// emoji se randează inconsistent pe Windows.
const MARKET = { ro: 'RO', ch: 'CH', both: 'RO · CH' }
const DEMO_URL = {
  'close-cockpit': '/demos/close-cockpit',
  'excel-to-app': '/demos/excel-to-app',
}

function Fade({ children, className = '', delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, ease: EASE, delay }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export default function DepartmentPage({ slug }) {
  const locale = useLocale()
  const t = useT().dept
  const data = getDepartment(slug, locale)
  const seo = getSeo(locale)
  return (
    <>
      <Seo
        title={seo.deptTitle(data)}
        description={seo.deptDescription(data)}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: `${data.label} software & automation`,
          serviceType: `Custom ${data.label} software`,
          provider: { '@type': 'Organization', name: 'vorba.dev', url: SITE },
          areaServed: ['United States', 'Switzerland', 'Romania'],
          description: data.hero,
        }}
      />

      {/* HERO — dark (forest via BgSwitch), transparent ca crossfade-ul global să treacă prin el */}
      <section data-theme="dark" className="relative flex min-h-screen items-center overflow-hidden px-5 text-cream md:px-10">
        <div className="mx-auto w-full max-w-site">
          <L
            to="/"
            className="mb-8 inline-flex min-h-[44px] items-center font-mono text-[12px] uppercase tracking-[0.1em] text-cream-muted transition-colors duration-300 ease-quint hover:text-honey"
          >
            {t.back}
          </L>
          <p className="mb-6 font-mono text-[12px] uppercase tracking-[0.1em] text-honey">{data.label} ✦</p>
          <h1 className="display max-w-[18ch] text-[11vw] leading-[1.02] md:text-[64px]">
            <RevealLines onMount lines={[data.hero]} />
          </h1>
        </div>
      </section>

      {/* Zonă-tampon verde — lasă crossfade-ul global verde→alb să cadă pe spațiu gol,
          departe de textul hero-ului (ca pe homepage). */}
      <div data-theme="dark" aria-hidden className="h-[78vh] md:h-[80vh]" />

      {/* THE PROBLEMS WE SOLVE — light (bone via BgSwitch) */}
      <section data-theme="light" className="px-5 py-28 text-ink md:px-10 md:py-40">
        <div className="mx-auto max-w-site">
          <p className="eyebrow mb-12">{t.problems} ✦</p>
          <ul className="border-t border-ink/15">
            {data.problems.map((p, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
                className="flex items-start gap-4 border-b border-ink/15 py-7"
              >
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-honey" />
                <span className="max-w-[52ch] font-sans text-xl leading-[1.45] text-ink md:text-[22px]">{p.text}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHAT WE BUILD — light */}
      <section data-theme="light" className="px-5 pb-28 text-ink md:px-10 md:pb-40">
        <div className="mx-auto max-w-site">
          <p className="eyebrow mb-12">{t.build} ✦</p>
          <ul className="grid gap-x-12 gap-y-5 md:grid-cols-2">
            {data.build.map((b, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
                className="flex items-start gap-3"
              >
                <span className="pt-1 font-mono text-ink-soft">→</span>
                <span className="font-sans text-lg leading-[1.5] text-ink md:text-xl">{b}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHY CUSTOM WINS HERE — light */}
      <section data-theme="light" className="px-5 pb-28 text-ink md:px-10 md:pb-40">
        <div className="mx-auto max-w-3xl">
          <p className="eyebrow mb-8">{t.why} ✦</p>
          <Fade>
            <p className="max-w-[34ch] font-sans text-2xl leading-[1.35] text-ink md:text-[32px]">{data.why}</p>
          </Fade>
        </div>
      </section>

      {/* CTA — light */}
      <section data-theme="light" className="px-5 pb-32 text-ink md:px-10 md:pb-48">
        <div className="mx-auto grid max-w-site gap-12 md:grid-cols-2 md:items-center">
          <Fade>
            <p className="eyebrow mb-8">{t.getStarted} ✦</p>
            <h2 className="display text-4xl leading-[1.02] md:text-5xl">{t.ctaHeading}</h2>
            <p className="mt-5 max-w-md text-lg text-ink-soft">{t.ctaSub}</p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              {...calAttrs}
              className="mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-ink px-6 py-3 font-mono text-xs uppercase tracking-[0.08em] text-cream transition-colors duration-300 ease-quint hover:bg-honey hover:text-ink"
            >
              {t.cta} <span aria-hidden>→</span>
            </a>
          </Fade>

          {data.demoKey ? (
            <Fade delay={0.1}>
              <div className="overflow-hidden rounded-xl border border-ink/15 shadow-xl">
                <DemoPreview demoKey={data.demoKey} />
              </div>
              {DEMO_URL[data.demoKey] ? (
                <a
                  href={DEMO_URL[data.demoKey]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 font-mono text-xs uppercase tracking-[0.08em] text-ink underline-offset-4 transition-colors duration-300 ease-quint hover:underline hover:decoration-honey"
                >
                  {t.seeDemo} <span aria-hidden>↗</span>
                </a>
              ) : (
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.08em] text-ink-soft">{t.conceptPreview}</p>
              )}
            </Fade>
          ) : null}
        </div>
      </section>
    </>
  )
}
